import React, { useState, useEffect } from 'react';
import {
  getClientsList,
  createClient,
  toggleClientStatus,
  resetClientPassword,
  updateClient,
  updateClientQuota,
  getOrganizationsList,
  uploadOrganizationImage,
} from '../services/api';
import {
  Plus,
  Search,
  RotateCcw,
  Eye,
  EyeOff,
  KeyRound,
  UserCog,
  BarChart3,
  X,
  Upload,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';

// Hoisted to module scope on purpose. When this lived inside ClientList's
// body, every parent re-render (e.g. on each keystroke into a child input)
// created a brand-new `Modal` function reference. React's reconciler treats
// a new function as a different component type, unmounts the existing
// subtree (inputs included), and remounts a fresh one — wiping focus.
// Moving it out keeps the same component identity across renders, so React
// reconciles in place and the input keeps focus while you type.
const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-ink-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-ink-100">{title}</h3>
          <button onClick={onClose} className="text-ink-500 hover:text-ink-300">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Create form
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    organization_id: '',
  });

  // Reset password form
  const [resetForm, setResetForm] = useState({
    new_password: '',
    send_email: false,
  });

  // Edit form
  const [editForm, setEditForm] = useState({
    full_name: '',
    phone: '',
    email: '',
  });

  // Quota form
  const [quotaForm, setQuotaForm] = useState({
    interview_quota: '',
    reset_interviews_used: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFilteredClients(
        clients.filter(
          (c) =>
            c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.organization_name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredClients(clients);
    }
  }, [searchTerm, clients]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const data = await getClientsList();
      setClients(data);
      setFilteredClients(data);
      setError('');
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const data = await getOrganizationsList();
      setOrganizations(data);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  // --- Create Client ---
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.password || !createForm.full_name || !createForm.organization_id) {
      setError('Please fill all required fields.');
      return;
    }
    try {
      setSubmitting(true);
      await createClient(createForm);
      setSuccess('Client created successfully');
      setShowCreateModal(false);
      setCreateForm({ email: '', password: '', full_name: '', phone: '', organization_id: '' });
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create client');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Toggle Status ---
  const handleToggleStatus = async (client) => {
    try {
      await toggleClientStatus(client.user_id);
      setSuccess(`Client ${client.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to toggle status');
    }
  };

  // --- Reset Password ---
  const openResetPasswordModal = (client) => {
    setSelectedClient(client);
    setResetForm({ new_password: '', send_email: false });
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetForm.new_password) {
      setError('Please enter a new password.');
      return;
    }
    try {
      setSubmitting(true);
      await resetClientPassword(selectedClient.user_id, resetForm);
      setSuccess(`Password reset for ${selectedClient.email}`);
      setShowResetPasswordModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let pass = '';
    for (let i = 0; i < 12; i++) pass += chars.charAt(Math.floor(Math.random() * chars.length));
    setResetForm({ ...resetForm, new_password: pass });
  };

  // --- Edit Client ---
  const openEditModal = (client) => {
    setSelectedClient(client);
    setEditForm({
      full_name: client.full_name || '',
      phone: client.phone || '',
      email: client.email || '',
    });
    setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await updateClient(selectedClient.user_id, editForm);
      setSuccess('Client updated successfully');
      setShowEditModal(false);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update client');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Image Upload ---
  const handleImageUpload = async (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('organization_id', selectedClient.organization_id);
      formData.append('image_type', imageType);
      await uploadOrganizationImage(formData);
      setSuccess(`${imageType.charAt(0).toUpperCase() + imageType.slice(1)} uploaded successfully`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to upload ${imageType}`);
    }
  };

  // --- Quota ---
  const openQuotaModal = (client) => {
    setSelectedClient(client);
    setQuotaForm({
      interview_quota: client.interview_quota ?? '',
      reset_interviews_used: false,
    });
    setShowQuotaModal(true);
  };

  const handleQuotaUpdate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await updateClientQuota(selectedClient.organization_id, {
        interview_quota: quotaForm.interview_quota === '' ? null : parseInt(quotaForm.interview_quota),
        reset_interviews_used: quotaForm.reset_interviews_used,
      });
      setSuccess('Quota updated successfully');
      setShowQuotaModal(false);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update quota');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Helpers ---
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getQuotaDisplay = (client) => {
    const used = client.interviews_used || 0;
    const quota = client.interview_quota;
    if (quota === null || quota === undefined) return `${used} / Unlimited`;
    return `${used} / ${quota}`;
  };

  const getQuotaColor = (client) => {
    const quota = client.interview_quota;
    if (quota === null || quota === undefined) return 'text-ink-300';
    const used = client.interviews_used || 0;
    if (used >= quota) return 'text-red-600 font-semibold';
    if (used >= quota * 0.8) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div>
      {/* Alerts */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-ink-100">Client Management</h2>
          <p className="text-sm text-ink-400 mt-1">{clients.length} client(s) registered</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 w-64"
            />
          </div>
          <button
            onClick={() => {
              setCreateForm({ email: '', password: '', full_name: '', phone: '', organization_id: '' });
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Client
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-ink-400">Loading clients...</div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-12 text-ink-400">
          {searchTerm ? 'No clients match your search.' : 'No clients found. Create your first client.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-ink-800 border-b">
                <th className="text-left px-4 py-3 font-medium text-ink-300">Client</th>
                <th className="text-left px-4 py-3 font-medium text-ink-300">Organization</th>
                <th className="text-left px-4 py-3 font-medium text-ink-300">Status</th>
                <th className="text-left px-4 py-3 font-medium text-ink-300">Last Login</th>
                <th className="text-left px-4 py-3 font-medium text-ink-300">Interviews</th>
                <th className="text-left px-4 py-3 font-medium text-ink-300">Candidates</th>
                <th className="text-right px-4 py-3 font-medium text-ink-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.user_id} className="border-b hover:bg-ink-800 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {client.logo_url ? (
                        <img
                          src={client.logo_url}
                          alt="logo"
                          className="w-8 h-8 rounded-full object-cover border"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold text-xs">
                          {client.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-ink-100">{client.full_name}</div>
                        <div className="text-ink-400 text-xs">{client.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-300">{client.organization_name}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        client.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {client.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-400 text-xs">{formatDate(client.last_login)}</td>
                  <td className="px-4 py-3">
                    <span className={getQuotaColor(client)}>{getQuotaDisplay(client)}</span>
                  </td>
                  <td className="px-4 py-3 text-ink-300">{client.candidate_count}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEditModal(client)}
                        title="Edit Client"
                        className="p-1.5 text-ink-400 hover:text-brand-400 hover:bg-brand-500/10 rounded"
                      >
                        <UserCog className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openResetPasswordModal(client)}
                        title="Reset Password"
                        className="p-1.5 text-ink-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openQuotaModal(client)}
                        title="Manage Quota"
                        className="p-1.5 text-ink-400 hover:text-orange-600 hover:bg-orange-50 rounded"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(client)}
                        title={client.is_active ? 'Deactivate' : 'Activate'}
                        className={`p-1.5 rounded ${
                          client.is_active
                            ? 'text-ink-400 hover:text-red-600 hover:bg-red-50'
                            : 'text-ink-400 hover:text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {client.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Client Modal */}
      <Modal show={showCreateModal} onClose={() => setShowCreateModal(false)} title="Add New Client">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Full Name *</label>
            <input
              type="text"
              value={createForm.full_name}
              onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
              className="w-full px-3 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Email *</label>
            <input
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              className="w-full px-3 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Password *</label>
            <input
              type="text"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              className="w-full px-3 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Phone</label>
            <input
              type="text"
              value={createForm.phone}
              onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
              className="w-full px-3 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Organization *</label>
            <select
              value={createForm.organization_id}
              onChange={(e) => setCreateForm({ ...createForm, organization_id: e.target.value })}
              className="w-full px-3 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              required
            >
              <option value="">Select Organization</option>
              {organizations.map((org) => (
                <option key={org.organization_id} value={org.organization_id}>
                  {org.organizationName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="px-4 py-2 text-sm text-ink-300 border border-ink-700 rounded-lg hover:bg-ink-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        show={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        title={`Reset Password - ${selectedClient?.full_name || ''}`}
      >
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">New Password *</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={resetForm.new_password}
                  onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })}
                  className="w-full px-3 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button
                type="button"
                onClick={generateRandomPassword}
                className="px-3 py-2 text-sm border border-ink-700 rounded-lg hover:bg-ink-800 flex items-center gap-1"
                title="Generate Random Password"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="send_email"
              checked={resetForm.send_email}
              onChange={(e) => setResetForm({ ...resetForm, send_email: e.target.checked })}
              className="rounded border-ink-700 text-purple-600 focus:ring-purple-500"
            />
            <label htmlFor="send_email" className="text-sm text-ink-300">
              Send new password via email
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowResetPasswordModal(false)}
              className="px-4 py-2 text-sm text-ink-300 border border-ink-700 rounded-lg hover:bg-ink-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {submitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Client Modal */}
      <Modal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={`Edit Client - ${selectedClient?.full_name || ''}`}
      >
        <form onSubmit={handleEdit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Full Name</label>
            <input
              type="text"
              value={editForm.full_name}
              onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
              className="w-full px-3 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Email</label>
            <input
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              className="w-full px-3 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Phone</label>
            <input
              type="text"
              value={editForm.phone}
              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              className="w-full px-3 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          {/* Logo & Banner Upload */}
          {selectedClient?.organization_id && (
            <>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">Organization Logo</label>
                <div className="flex items-center gap-3">
                  {selectedClient.logo_url && (
                    <img src={selectedClient.logo_url} alt="Logo" className="w-12 h-12 rounded object-cover border" />
                  )}
                  <label className="flex items-center gap-2 px-3 py-2 text-sm border border-ink-700 rounded-lg hover:bg-ink-800 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Upload Logo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'logo')}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">Organization Banner</label>
                <div className="flex items-center gap-3">
                  {selectedClient.banner_url && (
                    <img src={selectedClient.banner_url} alt="Banner" className="w-24 h-12 rounded object-cover border" />
                  )}
                  <label className="flex items-center gap-2 px-3 py-2 text-sm border border-ink-700 rounded-lg hover:bg-ink-800 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Upload Banner
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'banner')}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowEditModal(false)}
              className="px-4 py-2 text-sm text-ink-300 border border-ink-700 rounded-lg hover:bg-ink-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm text-white bg-brand-500 rounded-lg hover:bg-brand-400 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Quota Management Modal */}
      <Modal
        show={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
        title={`Interview Quota - ${selectedClient?.organization_name || ''}`}
      >
        <form onSubmit={handleQuotaUpdate} className="space-y-4">
          {/* Current Usage */}
          <div className="bg-ink-800 p-3 rounded-lg">
            <div className="text-sm text-ink-300">Current Usage</div>
            <div className={`text-lg font-semibold ${getQuotaColor(selectedClient || {})}`}>
              {getQuotaDisplay(selectedClient || {})}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">
              Monthly Interview Quota
            </label>
            <input
              type="number"
              min="0"
              placeholder="Leave empty for unlimited"
              value={quotaForm.interview_quota}
              onChange={(e) => setQuotaForm({ ...quotaForm, interview_quota: e.target.value })}
              className="w-full px-3 py-2 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <p className="text-xs text-ink-400 mt-1">Leave empty for unlimited interviews</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="reset_usage"
              checked={quotaForm.reset_interviews_used}
              onChange={(e) => setQuotaForm({ ...quotaForm, reset_interviews_used: e.target.checked })}
              className="rounded border-ink-700 text-orange-600 focus:ring-orange-500"
            />
            <label htmlFor="reset_usage" className="text-sm text-ink-300">
              Reset usage counter to 0
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowQuotaModal(false)}
              className="px-4 py-2 text-sm text-ink-300 border border-ink-700 rounded-lg hover:bg-ink-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Update Quota'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ClientList;
